import { Injectable } from '@angular/core';
import { PreloadingStrategy, Route } from '@angular/router';
import { Observable, of, timer } from 'rxjs';
import { mergeMap } from 'rxjs/operators';

/**
 * PERF: navigation speed. Lazy routes keep the initial bundle small, but the first
 * visit to each route then pays a network round-trip for its chunk. This strategy
 * preloads ONLY routes flagged `data: { preload: true }` — the daily-use screens
 * (dashboard, orders, products, chat, order-create) — without re-bloating main.js
 * (unlike PreloadAllModules, which fetches all 41 lazy chunks).
 *
 * It STAGGERS the preloads instead of firing them all at once: each flagged route
 * starts a few seconds after the previous one, so the daily screens warm up in the
 * background one-at-a-time and never burst-download (which competed with the first
 * route's render + API calls). The first preload waits FIRST_DELAY so it always
 * yields to initial paint.
 */
@Injectable({ providedIn: 'root' })
export class SelectivePreloadingStrategy implements PreloadingStrategy {
    private static readonly FIRST_DELAY = 4000; // ms before the first preload starts
    private static readonly GAP = 3000;         // ms between successive preloads
    private index = 0;                          // assigns each flagged route a slot

    preload(route: Route, load: () => Observable<any>): Observable<any> {
        if (route.data && route.data.preload) {
            const delay = SelectivePreloadingStrategy.FIRST_DELAY
                + this.index * SelectivePreloadingStrategy.GAP;
            this.index++;
            return timer(delay).pipe(mergeMap(() => load()));
        }
        return of(null);
    }
}
